import React, { useState, useEffect } from 'react';
import axios from "axios";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Snackbar,
  CircularProgress,
  Grid,
  Avatar,
  Divider,
  InputAdornment,
  Grow,
  Fade,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  SelectChangeEvent,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CheckCircle as CheckCircleIcon,
  CalendarToday as CalendarIcon,
  Public as PublicIcon,
  Transgender as TransgenderIcon,
  PinDrop as PinDropIcon,
  AssignmentInd as AssignmentIndIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useParams } from 'react-router-dom';
import { getWorkerProfileData, submitUpdatedProfileData } from '../../api/apiCall';

interface ApiProfileData {
  token: string;
  name: string;
  surname: string;
  email: string;
  phoneNumber: string;
  pts: string;
  postCode: string;
  location: string;
  driver: boolean;
  workRoles: string;
  customIds: string;
  notes: string;
  dateOfBirth: string;
  nationality: string;
  gender: string;
  hasDisability: boolean;
  disabilityDetails: string | null;
  hasCriminalRecord: boolean;
  criminalRecordSpent: boolean | null;
}

interface ApiResponse {
  data: ApiProfileData;
  success: boolean;
  message: string;
  statusCode: number;
  errors: any;
  requestId: string;
  timestamp: string;
  version: string;
}

interface UserData {
  fullName: string;
  pts: string;
  email: string;
  dateOfBirth: string;
  postcode: string;
  phone: string;
  gender: string;
  nationality: string;
  address: string;
  driver: 'yes' | 'no' | '';
  workRoles: string;
  customIds: string;
  notes: string;
  hasDisability: 'yes' | 'no' | '';
  disabilityDetails: string;
  hasCriminalRecord: 'yes' | 'no' | '';
  isConvictionSpent: 'yes' | 'no' | '';
}

interface FormErrors {
  fullName?: string;
  pts?: string;
  email?: string;
  dateOfBirth?: string;
  postcode?: string;
  phone?: string;
  gender?: string;
  nationality?: string;
  address?: string;
  driver?: string;
  workRoles?: string;
  customIds?: string;
  notes?: string;
  hasDisability?: string;
  disabilityDetails?: string;
  hasCriminalRecord?: string;
  criminalRecordDetails?: string;
  isConvictionSpent?: string;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info';
}

interface UpdateResponse {
  success: boolean;
  message: string;
}

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  background: 'linear-gradient(135deg, #ffffff 0%, #f5f7fe 100%)',
  boxShadow: '0 20px 40px -12px rgba(0,0,0,0.15)',
  position: 'relative',
  overflow: 'hidden',
}));

const fetchUserDataByToken = async (token: string): Promise<UserData> => {
  try {
    const response: any = await getWorkerProfileData(token);
    const apiResponse: ApiResponse = response;

    if (!apiResponse.success) {
      throw new Error(apiResponse.message || 'Invalid or expired link');
    }

    const profile = apiResponse.data;

    let dob = '';
    if (profile.dateOfBirth) {
      const match = profile.dateOfBirth.match(/^\d{4}-\d{2}-\d{2}/);
      if (match) {
        dob = match[0];
      } else {
        const date = new Date(profile.dateOfBirth);
        if (!isNaN(date.getTime())) {
          dob = date.toISOString().split('T')[0];
        }
      }
    }

    return {
      fullName: `${profile.name} ${profile.surname}`.trim(),
      pts: profile.pts,
      email: profile.email,
      dateOfBirth: dob,
      postcode: profile.postCode,
      phone: profile.phoneNumber,
      gender: profile.gender,
      nationality: profile.nationality,
      address: profile.location,
      driver: profile.driver ? 'yes' : 'no',
      workRoles: profile.workRoles || '',
      customIds: profile.customIds || '',
      notes: profile.notes || '',
      hasDisability: profile.hasDisability ? 'yes' : 'no',
      disabilityDetails: profile.disabilityDetails || '',
      hasCriminalRecord: profile.hasCriminalRecord ? 'yes' : 'no',
      isConvictionSpent: profile.criminalRecordSpent === true ? 'yes' : profile.criminalRecordSpent === false ? 'no' : '',
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.message || error.message || 'Failed to fetch profile data';
      throw new Error(message);
    }
    throw error;
  }
};

const updateUserDataAPI = async (token: string, userData: UserData): Promise<UpdateResponse> => {
  const payload = {
    token: token,
    name: userData.fullName.split(' ')[0] || '',
    surname: userData.fullName.split(' ').slice(1).join(' ') || '',
    email: userData.email,
    phoneNumber: userData.phone,
    pts: userData.pts,
    postCode: userData.postcode,
    location: userData.address,
    dateOfBirth: userData.dateOfBirth,
    nationality: userData.nationality,
    gender: userData.gender,
    driver: userData.driver === 'yes',
    workRoles: userData.workRoles || '',
    customIds: userData.customIds || '',
    notes: userData.notes || '',
    hasDisability: userData.hasDisability === 'yes',
    disabilityDetails: userData.disabilityDetails || null,
    hasCriminalRecord: userData.hasCriminalRecord === 'yes',
    criminalRecordSpent: userData.isConvictionSpent === 'yes' ? true : userData.isConvictionSpent === 'no' ? false : null,
  };

  const response: any = await submitUpdatedProfileData(token, payload);

  return {
    success: response.data.success,
    message: response.data.message,
  };
};

const UpdateDataPage = () => {
  const { token } = useParams<{ token: string }>();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [userData, setUserData] = useState<UserData>({
    fullName: '',
    pts: '',
    email: '',
    dateOfBirth: '',
    postcode: '',
    phone: '',
    gender: '',
    nationality: '',
    address: '',
    driver: '',
    workRoles: '',
    customIds: '',
    notes: '',
    hasDisability: '',
    disabilityDetails: '',
    hasCriminalRecord: '',
    isConvictionSpent: '',
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [updateSuccess, setUpdateSuccess] = useState<boolean>(false);
  const [, setActiveStep] = useState<number>(0);
  const [showWarningModal, setShowWarningModal] = useState<boolean>(false);

  const genderOptions = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
    { value: 'Rather not say', label: 'Rather not say' },
    { value: 'Other', label: 'Other' },
  ];

  useEffect(() => {
    if (!token) {
      setError('No update token provided. The link you used is incomplete.');
      setLoading(false);
      return;
    }

    fetchUserDataByToken(token)
      .then((data) => {
        setUserData(data);
        setLoading(false);
        setActiveStep(1);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [token]);

  const validateFullName = (value: string): string => {
    if (!value.trim()) return 'Full name is required';
    if (value.trim().length < 2) return 'Name must be at least 2 characters';
    return '';
  };

  const validatePTS = (value: string): string => {
    if (!value.trim()) return 'PTS is required. If you do not have one, please enter N/A.';
    if (value.trim().length < 3) return 'PTS ID must be at least 3 characters';
    return '';
  };

  const validateEmail = (value: string): string => {
    if (!value.trim()) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return 'Please enter a valid email address';
    return '';
  };

  const validateDateOfBirth = (value: string): string => {
    if (!value) return 'Date of birth is required';
    const date = new Date(value);
    if (isNaN(date.getTime())) return 'Please enter a valid date';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date >= today) return 'Date of birth cannot be in the future';
    const age = today.getFullYear() - date.getFullYear();
    if (age > 120) return 'Please enter a valid date of birth';
    return '';
  };

  const validatePostcode = (value: string): string => {
    if (!value.trim()) return 'Postcode is required';
    const postcodeRegex = /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i;
    if (!postcodeRegex.test(value.trim())) return 'Please enter a valid postcode';
    return '';
  };

  const validatePhone = (value: string): string => {
    if (!value.trim()) return 'Phone number is required';
    const phoneRegex = /^[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{3,6}[-\s\.]?[0-9]{3,6}$/im;
    if (!phoneRegex.test(value)) return 'Please enter a valid phone number';
    return '';
  };

  const validateGender = (value: string): string => {
    if (!value) return 'Please select a gender';
    return '';
  };

  const validateNationality = (value: string): string => {
    if (!value.trim()) return 'Nationality is required';
    return '';
  };

  const validateAddress = (value: string): string => {
    if (!value.trim()) return 'Address is required';
    if (value.trim().length < 5) return 'Please enter a complete address';
    return '';
  };

  const validateDriver = (value: string): string => {
    if (!value) return 'Please select Yes or No';
    return '';
  };

  const validateHasDisability = (value: string): string => {
    if (!value) return 'Please select Yes or No';
    return '';
  };

  const validateDisabilityDetails = (value: string, hasDisability: string): string => {
    if (hasDisability === 'yes' && !value.trim()) return 'Please specify your disability';
    return '';
  };

  const validateHasCriminalRecord = (value: string): string => {
    if (!value) return 'Please select Yes or No';
    return '';
  };

  const validateIsConvictionSpent = (value: string, hasCriminalRecord: string): string => {
    if (hasCriminalRecord === 'yes' && !value) return 'Please indicate if the conviction is spent';
    return '';
  };

  const validateField = (name: keyof UserData, value: string, fullState?: UserData): string => {
    switch (name) {
      case 'fullName': return validateFullName(value);
      case 'pts': return validatePTS(value);
      case 'email': return validateEmail(value);
      case 'dateOfBirth': return validateDateOfBirth(value);
      case 'postcode': return validatePostcode(value);
      case 'phone': return validatePhone(value);
      case 'gender': return validateGender(value);
      case 'nationality': return validateNationality(value);
      case 'address': return validateAddress(value);
      case 'driver': return validateDriver(value);
      case 'hasDisability': return validateHasDisability(value);
      case 'disabilityDetails': return validateDisabilityDetails(value, fullState?.hasDisability || '');
      case 'hasCriminalRecord': return validateHasCriminalRecord(value);
      case 'isConvictionSpent': return validateIsConvictionSpent(value, fullState?.hasCriminalRecord || '');
      default: return '';
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setUserData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name as keyof FormErrors]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setUserData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name as keyof FormErrors]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUserData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name as keyof FormErrors]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
    if (name === 'hasDisability' && value === 'no') {
      setUserData((prev) => ({ ...prev, disabilityDetails: '' }));
      setFormErrors((prev) => ({ ...prev, disabilityDetails: '' }));
    }
    if (name === 'hasCriminalRecord' && value === 'no') {
      setUserData((prev) => ({ ...prev, criminalRecordDetails: '', isConvictionSpent: '' }));
      setFormErrors((prev) => ({ ...prev, criminalRecordDetails: '', isConvictionSpent: '' }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const error = validateField(name as keyof UserData, value, userData);
    setFormErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleSelectBlur = (field: keyof UserData, value: string) => {
    const error = validateField(field, value, userData);
    setFormErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleRadioBlur = (field: keyof UserData, value: string) => {
    const error = validateField(field, value, userData);
    setFormErrors((prev) => ({ ...prev, [field]: error }));
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    (Object.keys(userData) as Array<keyof UserData>).forEach((key) => {
      const error = validateField(key, userData[key], userData);
      if (error) errors[key] = error;
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isPTSValidNumber = (): boolean => {
    const ptsValue = userData.pts.trim();
    return /^\d+$/.test(ptsValue);
  };

  const isCSCSValid = (): boolean => {
    const customIdsValue = userData.customIds.trim();
    return /[a-zA-Z]/.test(customIdsValue) && /\d/.test(customIdsValue);
  };

  const needsWarning = (): boolean => {
    return !isPTSValidNumber() && !isCSCSValid();
  };

  const submitData = async () => {
    setIsSubmitting(true);
    try {
      const response = await updateUserDataAPI(token!, userData);
      setSnackbar({
        open: true,
        message: response.message,
        severity: 'success',
      });
      setUpdateSuccess(true);
      setActiveStep(2);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Update failed. Please try again.',
        severity: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (needsWarning()) {
      setShowWarningModal(true);
      return;
    }
    await submitData();
  };

  const handleContinueWithoutInfo = async () => {
    setShowWarningModal(false);
    await submitData();
  };

  const handleCloseWarningModal = () => {
    setShowWarningModal(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box textAlign="center">
          <CircularProgress size={60} thickness={4} sx={{ mb: 2 }} />
          <Typography variant="h6" color="textSecondary">
            Verifying your link...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Grow in>
          <Paper elevation={3} sx={{ p: 5, textAlign: 'center', borderRadius: 4 }}>
            <Typography variant="h5" gutterBottom color="error">
              Link Error
            </Typography>
            <Typography variant="body1" color="textSecondary" paragraph>
              {error}
            </Typography>
            {/* <Button variant="contained" color="primary" href="/request-update" sx={{ mt: 2, borderRadius: 2, boxShadow: 'none' }}>
              Request New Link
            </Button> */}
          </Paper>
        </Grow>
      </Container>
    );
  }

  if (updateSuccess) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Fade in>
          <StyledPaper>
            <Box display="flex" justifyContent="center" mb={3}>
              <Avatar sx={{ bgcolor: 'success.main', width: 80, height: 80, boxShadow: 3 }}>
                <CheckCircleIcon sx={{ fontSize: 50 }} />
              </Avatar>
            </Box>
            <Typography variant="h4" align="center" gutterBottom fontWeight="bold" color="primary.dark">
              Update Complete!
            </Typography>
            <Typography variant="body1" align="center" color="textSecondary" paragraph sx={{ mb: 4 }}>
              Your personal information has been updated successfully. You may now close this page.
            </Typography>
          </StyledPaper>
        </Fade>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4, minHeight: '100vh' }}>
      <Grow in timeout={500}>
        <Box>

          <StyledPaper>
            <Typography variant="h5" align="center" gutterBottom fontWeight="bold">
              Update Your Information
            </Typography>
            <Typography variant="body2" align="center" color="textSecondary" sx={{ mb: 4 }}>
              Please review and update your details below. All changes will be saved securely.
            </Typography>

            <Divider sx={{ mb: 4 }} />

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    placeholder="Full Name"
                    label="Full Name"
                    name="fullName"
                    value={userData.fullName}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={!!formErrors.fullName}
                    helperText={formErrors.fullName || 'Your legal full name'}
                    disabled={isSubmitting}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="PTS"
                    placeholder="PTS"
                    name="pts"
                    value={userData.pts}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={!!formErrors.pts}
                    helperText={formErrors.pts || 'Your sentinel number'}
                    disabled={isSubmitting}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AssignmentIndIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl component="fieldset" error={!!formErrors.driver}>
                    <FormLabel component="legend">Are you a driver?</FormLabel>
                    <RadioGroup
                      row
                      name="driver"
                      value={userData.driver}
                      onChange={handleRadioChange}
                      onBlur={() => handleRadioBlur('driver', userData.driver)}
                    >
                      <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                      <FormControlLabel value="no" control={<Radio />} label="No" />
                    </RadioGroup>
                    {formErrors.driver && <FormHelperText>{formErrors.driver}</FormHelperText>}
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Custom Identifiers"
                    name="customIds"
                    value={userData.customIds}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={!!formErrors.customIds}
                    helperText={formErrors.customIds || 'Please provide other identifier separated by commas e.g. "CSCS Blue: 12345, CSCS Gold: 67890"'}
                    disabled={isSubmitting}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    placeholder="Email Address"
                    label="Email Address"
                    name="email"
                    type="email"
                    value={userData.email}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={!!formErrors.email}
                    helperText={formErrors.email || ""}
                    disabled={true}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Date of Birth"
                    name="dateOfBirth"
                    type="date"
                    value={userData.dateOfBirth}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={!!formErrors.dateOfBirth}
                    helperText={formErrors.dateOfBirth || 'DD-MM-YYYY'}
                    disabled={isSubmitting}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiInputBase-root': { height: 56 },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    placeholder="Phone Number"
                    name="phone"
                    value={userData.phone}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={!!formErrors.phone}
                    helperText={formErrors.phone || 'Include country code'}
                    disabled={isSubmitting}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Postcode"
                    placeholder="Postcode"
                    name="postcode"
                    value={userData.postcode}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={!!formErrors.postcode}
                    helperText={formErrors.postcode || 'e.g., SW1A 1AA'}
                    disabled={isSubmitting}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PinDropIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!formErrors.gender} disabled={isSubmitting}>
                    <InputLabel id="gender-label">Gender</InputLabel>
                    <Select
                      labelId="gender-label"
                      name="gender"
                      value={userData.gender}
                      label="Gender"
                      placeholder="Gender"
                      onChange={handleSelectChange}
                      onBlur={() => handleSelectBlur('gender', userData.gender)}
                      startAdornment={
                        <InputAdornment position="start">
                          <TransgenderIcon color="action" sx={{ mr: 1 }} />
                        </InputAdornment>
                      }
                    >
                      {genderOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {formErrors.gender && <FormHelperText>{formErrors.gender}</FormHelperText>}
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nationality"
                    placeholder="Nationality"
                    name="nationality"
                    value={userData.nationality}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={!!formErrors.nationality}
                    helperText={formErrors.nationality || 'Your country of citizenship'}
                    disabled={isSubmitting}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PublicIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Address / Location"
                    name="address"
                    placeholder="Street, city, depot"
                    value={userData.address}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={!!formErrors.address}
                    helperText={formErrors.address || 'Your work location or home address'}
                    disabled={isSubmitting}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Work Role"
                    name="workRoles"
                    placeholder="Work role"
                    value={userData.workRoles}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={!!formErrors.workRoles}
                    helperText={formErrors.workRoles || 'Enter one or more work roles separated by commas e.g. Banksman, Trackman'}
                    disabled={isSubmitting}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <WorkIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    name="notes"
                    placeholder="Enter any additional information you think is relevant"
                    value={userData.notes}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    multiline
                    rows={3}
                    error={!!formErrors.notes}
                    helperText={formErrors.notes || 'Enter any additional information you think is relevant'}
                    disabled={isSubmitting}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }}>
                    <Typography variant="body2" color="textSecondary">DISABILITY INFORMATION</Typography>
                  </Divider>
                </Grid>

                <Grid item xs={12}>
                  <FormControl component="fieldset" error={!!formErrors.hasDisability}>
                    <FormLabel component="legend">Do you have a disability?</FormLabel>
                    <RadioGroup
                      row
                      name="hasDisability"
                      value={userData.hasDisability}
                      onChange={handleRadioChange}
                      onBlur={() => handleRadioBlur('hasDisability', userData.hasDisability)}
                    >
                      <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                      <FormControlLabel value="no" control={<Radio />} label="No" />
                    </RadioGroup>
                    {formErrors.hasDisability && <FormHelperText>{formErrors.hasDisability}</FormHelperText>}
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Collapse in={userData.hasDisability === 'yes'}>
                    <TextField
                      fullWidth
                      label="Please specify your disability"
                      name="disabilityDetails"
                      value={userData.disabilityDetails}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      error={!!formErrors.disabilityDetails}
                      helperText={formErrors.disabilityDetails || 'Describe any disability, condition, or access requirements'}
                      disabled={isSubmitting}
                      multiline
                      rows={2}
                    />
                  </Collapse>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }}>
                    <Typography variant="body2" color="textSecondary">CRIMINAL RECORD INFORMATION</Typography>
                  </Divider>
                </Grid>

                <Grid item xs={12}>
                  <FormControl component="fieldset" error={!!formErrors.hasCriminalRecord}>
                    <FormLabel component="legend">Do you have a criminal record?</FormLabel>
                    <RadioGroup
                      row
                      name="hasCriminalRecord"
                      value={userData.hasCriminalRecord}
                      onChange={handleRadioChange}
                      onBlur={() => handleRadioBlur('hasCriminalRecord', userData.hasCriminalRecord)}
                    >
                      <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                      <FormControlLabel value="no" control={<Radio />} label="No" />
                    </RadioGroup>
                    {formErrors.hasCriminalRecord && <FormHelperText>{formErrors.hasCriminalRecord}</FormHelperText>}
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Collapse in={userData.hasCriminalRecord === 'yes'}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControl component="fieldset" error={!!formErrors.isConvictionSpent}>
                          <FormLabel component="legend">Is this conviction spent?</FormLabel>
                          <RadioGroup
                            row
                            name="isConvictionSpent"
                            value={userData.isConvictionSpent}
                            onChange={handleRadioChange}
                            onBlur={() => handleRadioBlur('isConvictionSpent', userData.isConvictionSpent)}
                          >
                            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                            <FormControlLabel value="no" control={<Radio />} label="No" />
                          </RadioGroup>
                          {formErrors.isConvictionSpent && <FormHelperText>{formErrors.isConvictionSpent}</FormHelperText>}
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Collapse>
                </Grid>
              </Grid>

              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Button variant="outlined" onClick={() => window.history.back()} disabled={isSubmitting} sx={{ borderRadius: 2 }}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                  sx={{
                    px: 4,
                    borderRadius: 2,
                    boxShadow: "none",
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <CircularProgress size={24} sx={{ mr: 1, color: 'white' }} />
                      Updating...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </Button>
              </Box>
            </form>

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="caption" color="textSecondary">
                This link is secure and expires in 72 hours. Your data is encrypted.
              </Typography>
            </Box>
          </StyledPaper>
        </Box>
      </Grow>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={showWarningModal}
        onClose={handleCloseWarningModal}
        aria-labelledby="warning-modal-title"
        aria-describedby="warning-modal-description"
      >
        <DialogTitle id="warning-modal-title">Missing Information</DialogTitle>
        <DialogContent>
          <DialogContentText id="warning-modal-description">
            You have not entered a valid PTS number or CSCS/CPCS card number.
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          </DialogContentText>

          <DialogContentText id="warning-modal-description">
            These qualifications may be required for railway or highway projects.
            Continuing without them could limit the roles or sites you are eligible to work on.
          </DialogContentText>

        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseWarningModal} color="primary">
            Cancel
          </Button>
          <Button onClick={handleContinueWithoutInfo} color="primary" autoFocus>
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UpdateDataPage;